import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { getHookClaims } from './auth-claims'
import { useAuthStore } from '@/store/auth'
import { useUserStore } from '@/store/user'

/**
 * Canonical auth bridge (#1112 / #1113).
 *
 * supabase-js owns the session (localStorage, autoRefreshToken). This module
 * registers the SINGLE source of truth that reacts to GoTrue auth state:
 *   (a) syncs the Zustand auth store from the hook claims minted by
 *       custom_access_token_hook (user_id / organization_id / user_role), and
 *   (b) re-authenticates the Realtime socket with the current access token via
 *       supabase.realtime.setAuth(...) so postgres_changes stop being rejected
 *       by RLS as an anon socket.
 *
 * It is registered once at client init (see lib/supabase.ts). A silent
 * TOKEN_REFRESHED (SDK-owned ~15-min rotation) re-auths Realtime with the
 * rotated token WITHOUT logging the user out — the store identity survives.
 *
 * INITIAL_SESSION (NOT SIGNED_IN) is what supabase-js emits when it rehydrates
 * a persisted localStorage session on a hard page reload, so it must re-auth
 * Realtime too — otherwise the socket stays anon after a refresh and RLS keeps
 * rejecting postgres_changes until the next rotation/click (the #1112 symptom).
 * When it fires with no session (logged-out load) the `if (session)` guard makes
 * it a harmless no-op.
 */

let subscription: { unsubscribe: () => void } | null = null

/**
 * Reads the app-level claims the access token hook mints as top-level JWT
 * claims in the access token (see lib/auth-claims.ts) and pushes them into
 * the Zustand auth store.
 */
function syncStoreFromSession(session: Session): void {
  const { userId, organizationId, role } = getHookClaims(session)

  if (userId && organizationId) {
    useAuthStore.getState().setAuth(userId, organizationId, role)
  }
}

/**
 * Registers exactly ONE onAuthStateChange listener. Idempotent: a second call
 * tears down the previous subscription before re-registering.
 */
export function initAuthBridge(): { unsubscribe: () => void } {
  subscription?.unsubscribe()

  const result = supabase.auth.onAuthStateChange((event, session) => {
    switch (event) {
      case 'INITIAL_SESSION':
      case 'SIGNED_IN':
      case 'TOKEN_REFRESHED':
        if (session) {
          // (b) re-auth the Realtime socket with the (possibly rotated) JWT.
          supabase.realtime.setAuth(session.access_token)
          // (a) keep the Zustand identity in sync with the live session.
          syncStoreFromSession(session)
        }
        break
      case 'SIGNED_OUT':
        supabase.realtime.setAuth(null)
        useAuthStore.getState().reset()
        // Also drop the cached user profile: it carries PII and survives in
        // the persisted store on SDK-initiated sign-outs that never pass
        // through useLogoutMutation's cleanup.
        useUserStore.getState().reset()
        break
      default:
        break
    }
  })

  const sub = result?.data?.subscription ?? { unsubscribe: () => {} }
  subscription = sub
  return sub
}

export default initAuthBridge
